import styled from "styled-components";

/**
 * ============================================================================
 * THE HERO'S ANGLED FOOT LIVES ON A DECORATIVE LAYER, NOT ON THIS ELEMENT.
 * ============================================================================
 *
 * This is the D-049 fix applied to the one place that still carried the
 * construct D-049 was about. The wrapper in `Home.tsx` had `clip-path` on
 * itself, and because the notch is a PERCENTAGE of a block whose height had
 * tripled, it silently ate two of four Education cards. The same shape was
 * still here: `clip-path` on `HeroContainer`, `70% 95%` — a 5% notch, which is
 * 44px at 1280 and 59px at 390 and would be more the moment the hero grew.
 *
 * Nothing was clipped when this changed, and that is the point rather than a
 * reason to leave it: the audit measured the "Explore my Projects" button
 * clearing the boundary by 54px at 1280, 34px at 768 and **18px at 390**, with
 * the hero's right-hand column already 31-47px inside the wedge. Eighteen
 * pixels is one extra line of typed role. A decorative shape that clips
 * content is a bug waiting for the next line of copy.
 *
 * Moving the paint to a `::before` is byte-identical in shape — same polygon,
 * same 5%, same `--bg-elevated` — and it can never clip content again however
 * much the hero grows, because it no longer clips the element that HOLDS the
 * content. Verified by measuring notch depth relative to the hero's own bottom
 * edge at 25 columns: identical before and after, both themes, both widths.
 *
 * `isolation: isolate` is deliberate even though `z-index: 1` on a positioned
 * element already opens a stacking context. It makes the guarantee independent
 * of that z-index: without it, the layer's containment is a side effect of a
 * value someone could reasonably change while thinking only about layering,
 * and a `z-index: -1` layer that escapes its ancestor lands behind the page
 * ground and disappears (the D-049 failure, in the other direction).
 */
export const HeroContainer = styled.div`
	display: flex;
	justify-content: center;
	position: relative;
	isolation: isolate;
	padding: 80px 30px;
	@media (max-width: 1024px) {
		padding: 66px 16px;
	}
	@media (max-width: 640px) {
		padding: 32px 16px;
	}
	z-index: 1;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		z-index: -1;
		background: var(--bg-elevated);
		clip-path: polygon(0 0, 100% 0, 100% 100%, 70% 95%, 0 100%);
		pointer-events: none;
	}
`;

export const HeroBg = styled.div`
	position: absolute;
	display: flex;
	justify-content: end;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 100%;
	max-width: 1360px;
	overflow: hidden;
	padding: 0 30px;
	top: 50%;
	left: 50%;
	-webkit-transform: translateX(-50%) translateY(-50%);
	transform: translateX(-50%) translateY(-50%);

	@media (max-width: 1024px) {
		justify-content: center;
		padding: 0 0px;
	}
`;

export const HeroInnerContainer = styled.div`
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	max-width: 1100px;

	@media (max-width: 1024px) {
		flex-direction: column;
	}
`;
export const HeroLeftContainer = styled.div`
	width: 100%;
	order: 1;
	/* The container the designation's font size is measured against (D-080).
	   'inline-size' containment only — NOT 'size' — so the rail still takes its
	   height from its content. It carries no position:fixed descendant: the MUI
	   Modal below portals to <body>, which is what keeps 'contain: layout' from
	   becoming a containing block for it, i.e. the D-076 lightbox trap. */
	container-type: inline-size;
	@media (max-width: 1024px) {
		order: 2;
		margin-bottom: 30px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	@media (max-width: 640px) {
		order: 2;
		margin-bottom: 30px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
`;

export const HeroRightContainer = styled.div`
	width: 100%;
	display: flex;
	order: 2;
	justify-content: end;
	gap: 12px;
	@media (max-width: 1024px) {
		order: 1;
		justify-content: center;
		align-items: center;
		margin-bottom: 80px;
	}

	@media (max-width: 640px) {
		margin-bottom: 30px;
	}
`;

/**
 * THE HERO AVATAR. A 50% radius and a ring around it: the design says circle,
 * and until D-083 the element could not make one.
 *
 * `height: 100%` resolved to `auto` against an auto-height parent, so the box
 * took the picture's own aspect and `border-radius: 50%` drew an ELLIPSE — the
 * convocation photograph is 1600x1459, so it rendered 280x255.7 at 390 and was
 * squashed by 9% with `object-fit: fill`. Nine percent is small enough to look
 * like nothing and large enough to be wrong on a photograph of four people.
 *
 * `aspect-ratio` plus `object-fit: cover` fixes both halves at once: the box is
 * square so the radius is a circle, and the picture is CROPPED to it rather
 * than stretched. At 1.097:1 that crop is 8.8% of the width, taken evenly off
 * both sides.
 *
 * AND IT RESERVES THE SPACE, WHICH IS THE HALF NOBODY SEES. With no intrinsic
 * size declared, this box was ZERO until the image decoded and then 256-365px
 * — every element on the page below it moved, on every cold load. That shift
 * is also what made a contrast sweep read the ground under the hero CTA from
 * the wrong pixels. A width and an aspect ratio are both known before a single
 * byte of the JPEG arrives.
 */
export const Img = styled.img`
	position: relative;
	width: 100%;
	height: auto;
	aspect-ratio: 1 / 1;
	object-fit: cover;
	max-width: 400px;
	border-radius: 50%;
	border: 2px solid var(--accent-interactive);

	@media (max-width: 640px) {
		max-width: 280px;
	}
`;

/**
 * THE PAGE'S ONLY <h1>, as of D-042 — and it was a `styled.div` until then, so
 * the document had no h1 at all (found 2026-08-12 under D-032, fixed here
 * because "which heading leads" was really a markup question, not a content
 * one). This element already WAS the visually dominant heading: 50px, weight
 * 700, carrying the author's name at the top of "/". Promoting it is therefore
 * the change that adds no copy — the alternative, inventing a documentary
 * strapline, would have put words on the page that no source supports (II.3).
 *
 * `margin: 0` is what keeps the promotion invisible. As a <div> the margin
 * computed to zero; as an <h1> the UA default margin-block: 0.67em would apply
 * and push the hero down. Declaring it makes the element's box identical before
 * and after — verified in the parity fingerprint rather than assumed.
 */
export const Title = styled.h1`
	font-weight: 700;
	font-size: 50px;
	color: var(--text-primary);
	line-height: 68px;
	margin: 0;
	@media (max-width: 1024px) {
		text-align: center;
	}

	@media (max-width: 640px) {
		font-size: 40px;
		line-height: 48px;
		margin-bottom: 8px;
	}
`;

export const Header2 = styled.div`
	font-weight: 600;
	font-size: 35px;
	color: var(--text-primary);
	line-height: 68px;
	@media (max-width: 1024px) {
		text-align: center;
	}

	@media (max-width: 640px) {
		font-size: 28px;
		line-height: 40px;
		margin-bottom: 8px;
	}
`;

/**
 * ============================================================================
 * The designation line. ONE LINE AT EVERY WIDTH — owner ruling, D-080.
 * ============================================================================
 *
 * It used to reserve two lines on purpose (`min-height: 136px`, with a comment
 * saying the role "crosses the single-line width mid-word"). Measured with the
 * FULL string written into the span rather than captured mid-type, it wrapped
 * at TWELVE of eighteen widths: 320, 360, 375, 390, 412, 430, 480, 700, 1150,
 * 1280, 1440 and 1920. It fitted only at 540-640 and 768-1024.
 *
 * THE FIRST MEASUREMENT OF THIS WAS WRONG AND IS WORTH RECORDING. Counting
 * lines as height/line-height reported "2 lines" at 1280, where the span is
 * 437px inside a 550px row — which cannot be a wrap. A flex row whose child has
 * a taller line box inflates the parent without the text wrapping, and the
 * ratio cannot tell those apart. Counting the text's own client rects can, and
 * that is what the harness does now.
 *
 * WHY A CONTAINER QUERY AND NOT vw. The available width is NOT monotonic in
 * viewport width: it is 556px at 1024 and 437px at 1280, because the hero goes
 * two-column and the left rail narrows. A vw-based clamp cannot track a
 * container that shrinks as the viewport grows, so it would have to be
 * calibrated to the worst case and be needlessly small everywhere else.
 * `cqi` reads the actual rail.
 *
 * The coefficient is measured, not guessed: this typeface renders the string at
 * ~17.05x the font size (375px at 22px, 545px at 32px — the same ratio twice),
 * and the "I am a" prefix plus its gap costs about another 3.2x when inline.
 * 4.6cqi leaves margin on both. The 30px cap keeps the desktop size close to
 * the 32px it had; the 11px floor is only reached below 340px, where the rail
 * is 207px wide.
 *
 * `min-height` is gone rather than reduced. It existed to stop the hero
 * shifting as the line count changed while typing — with `white-space: nowrap`
 * the line count cannot change, so the floor has nothing left to protect and
 * would only reserve empty space.
 */
export const TextLoop = styled.div`
	font-weight: 600;
	font-size: clamp(11px, 4.6cqi, 30px);
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0 12px;
	color: var(--text-primary);
	line-height: 1.5;
	@media (max-width: 1024px) {
		text-align: center;
		justify-content: center;
	}
	@media (max-width: 640px) {
		margin-bottom: 16px;
	}
`;

/**
 * The fixed "I am a" lead-in. It is a flex item, so without `flex-shrink: 0`
 * it is squeezed below its own width by a long typed role and breaks across
 * lines as "I am" / "a". Any role longer than ~26 characters triggers this.
 */
export const TextLoopPrefix = styled.span`
	flex-shrink: 0;
	white-space: nowrap;
`;

/**
 * The typed hero role. `color` here is a FALLBACK, not what a reader sees —
 * and that distinction is the whole point of this comment.
 *
 * The element also carries `.gradient-text`, which sets
 * `-webkit-text-fill-color: transparent` and clips a coral gradient to the
 * glyphs. Text fill wins over `color` outright, so on every browser that
 * supports it these characters are painted by `--grad-accent-from/to` and this
 * declaration reaches nothing. Measured rather than reasoned: sampling the
 * glyph pixels returns 255,128,96 through 254,179,123 in dark and 190,70,11
 * through 194,66,12 in light — the two gradient ramps, with NO pixel of this
 * colour anywhere in the box.
 *
 * That matters because D-049 recorded a real AA failure here at 3.46:1,
 * `--accent-interactive` (#854ce6) on `--bg-elevated`. The arithmetic was right
 * and the input was not: it scored a declaration that never paints. The typed
 * role actually measures 6.97:1 in dark and 4.90:1 in light at its worst stop,
 * and has never failed AA.
 *
 * The token still changes, on the owner's ruling and because a fallback should
 * be the safe one: if the gradient ever fails to apply — a browser without
 * -webkit-text-fill-color, a stylesheet that did not load — this colour becomes
 * visible, and `--accent-interactive` is the FILL token, 3.46:1 as text on this
 * ground. `--accent-interactive-text` is the token for exactly this job and
 * measures 4.72:1. So the pixels do not move and the failure mode improves.
 */
export const Span = styled.span`
	color: var(--accent-interactive-text);
	cursor: pointer;
	/* THE GUARANTEE. Sizing alone gets the string to fit; this is what makes a
	   second line impossible if a longer role is ever added to the rotation —
	   it would overflow visibly rather than silently re-wrap the hero, which is
	   the failure mode worth having, because it is the one someone notices. */
	white-space: nowrap;
	/* A flex item's min-width is 'auto', so without this it refuses to shrink
	   below its content and pushes the row wider than the rail. */
	min-width: 0;
`;

export const SubTitle = styled.div`
	font-size: 20px;
	line-height: 32px;
	margin-bottom: 0.5rem;
	color: var(--text-hero-lead);

	@media (max-width: 1024px) {
		text-align: center;
	}

	@media (max-width: 640px) {
		font-size: 16px;
		line-height: 32px;
	}
`;

/**
 * ============================================================================
 * The hero's two calls to action, in one row that owns their alignment (D-080).
 * ============================================================================
 *
 * They had no shared container at all, and the measurements say what that
 * cost. At 390 the Resume button sat at x=45 and 300px wide while Explore sat
 * at x=87 and 217px wide — a 42px stagger and an 83px width difference between
 * two buttons meant to read as a pair. At 768 the stagger was 30px and their
 * heights differed by 26px. At 1280 they landed 300px apart horizontally with
 * tops 21px out of line and a NEGATIVE 41px gap, i.e. overlapping vertically.
 *
 * None of that was a styling accident: `MagicButton` carried `md:w-60` and
 * `md:mt-10`, so a shared control was setting its own width and its own 40px
 * top margin at >=768 — the hero's layout, decided inside a component the hero
 * does not own. Both are gone; this row decides.
 *
 * `flex-wrap: wrap` with a 300px basis is what makes it responsive without a
 * breakpoint: two abreast when the rail can hold them, stacked and full-width
 * when it cannot, and identical widths either way.
 */
export const HeroActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	gap: 16px;
	width: 100%;
	margin-top: 24px;

	/* Both children are laid out identically whatever element they are.
	   NO BACKTICKS IN THIS COMMENT: it is inside a styled-components template
	   literal, and one backtick here terminates the CSS and fails the build with
	   a syntax error pointing at the next property. The same warning is on the
	   AA sweep's collector for the same reason.
	
	   display:flex is the second half of align-items:stretch, and D-082 is the
	   record of what happens without it. One child is an <a> WRAPPING a button,
	   and stretching the wrapper does nothing for the control inside it: the
	   wrapper grew to the row height while MagicButton stayed at its own fixed
	   h-12, sitting at the top of a taller box. Every box measurement called
	   that correct — the two wrappers WERE identical — while the two LABELS sat
	   7px apart, the right-hand one with 14px of space above it and 28px below.
	   The same shape as D-080's grid finding: a stretched parent is not a
	   filled child. */
	> * {
		flex: 1 1 240px;
		max-width: 300px;
		min-width: 0;
		margin: 0;
		display: flex;
		/* THE SHARED FLOOR, and it is what makes the two match when the row
		   WRAPS. Side by side, align-items:stretch already equalises them
		   against the taller one. Stacked at 390 each control is alone on its
		   line, so there is nothing to stretch against and each falls back to
		   its own content height — 51px for one, 48px for the other. A floor
		   both clear removes the difference in the one layout stretch cannot
		   reach. */
		min-height: 56px;
	}

	/* Beats Tailwind's 'md:mt-10' and 'h-12' on specificity (0,1,1 vs 0,1,0)
	   without reaching for !important. The margin class is gone from the
	   component now anyway; height:100% is the live half, and it is what
	   makes the nested button fill the wrapper the row just stretched. */
	> * > button {
		margin-top: 0;
		/* height:auto UNSETS Tailwind's h-12, then align-self:stretch fills the
		   wrapper. The first attempt used height:100%, which is correct side by
		   side and COLLAPSES when the row wraps: the wrapper then has no
		   definite height, so 100% of auto resolved to min-content and the
		   button measured 22px against its neighbour's 51px. Stretch has no
		   such circularity. */
		height: auto;
		align-self: stretch;
	}

	@media (max-width: 1024px) {
		justify-content: center;
	}

	/* Matches ResumeButton's own 640px breakpoint, where its type drops to
	   18px. One floor across both controls at each size. */
	@media (max-width: 640px) {
		> * {
			min-height: 48px;
		}
	}
`;

export const ResumeButton = styled.a`
	-webkit-appearance: button;
	-moz-appearance: button;
	appearance: button;
	text-decoration: none;
	/* The row owns the width now (see HeroActions); 95% of an unknown parent
	   was what made this 300px while its neighbour was 217px. */
	width: 100%;
	max-width: 300px;
	text-align: center;
	/* CENTRED BY FLEX, NOT BY SYMMETRIC PADDING (D-082). Padding centres the
	   label only while the box hugs its content, and this box does not: the row
	   stretches it to match its neighbour. With padding alone the label stayed
	   put and drifted off the neighbour's baseline as soon as the heights
	   differed — which no box measurement can see. */
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 16px;
	color: var(--accent-interactive-contrast);
	border-radius: 20px;
	cursor: pointer;
	font-size: 20px;
	font-weight: 600;
	transition: all 0.2s ease-in-out !important;
	/* Tokenised in D-035. The three vendor-prefixed duplicates of the same
       gradient are gone with it: -moz- has not been needed since Firefox 16
       and -webkit- since Chrome 26, and each was simply overwritten by the
       next line anyway. The unprefixed value is what rendered. */
	background: linear-gradient(225deg, var(--grad-hero-from) 0%, var(--grad-hero-to) 100%);
	box-shadow: var(--shadow-hero-cta);
	&:hover {
		transform: scale(1.05);
		transition: all 0.4s ease-in-out;
		/* Was a single malformed declaration, with the filter sitting inside
           the box-shadow list: "box-shadow: 20px 20px 60px #1F2634, filter:
           brightness(1);". The whole declaration was invalid and dropped, so
           hover fell back to the base shadow and the filter never applied.
           Split into two valid declarations whose rendered result is
           identical: the shadow is the same value, and brightness(1) is a
           no-op. */
		box-shadow: var(--shadow-hero-cta);
		filter: brightness(1);
	}
	@media (max-width: 640px) {
		font-size: 18px;
	}
`;
